import { Injectable } from '@angular/core';
import { environment } from './../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  private readonly secretKey = environment.secrets.key;
  private readonly ivLength = Number(environment.secrets.ivLength);

  async encrypt(text: string): Promise<string> {
    const base64Key = this.secretKey;
    const keyBuffer = this.base64ToArrayBuffer(base64Key);
    const key = await window.crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-CBC' }, false, ['encrypt']);
    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    const encodedText = new TextEncoder().encode(text);
    const encrypted = await window.crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, encodedText);
    return this.arrayBufferToHex(iv) + ':' + this.arrayBufferToHex(encrypted);
  }

  async decrypt(token: string): Promise<string> {
    const [ivHex, encryptedHex] = token.split(':');

    const iv = this.hexToArrayBuffer(ivHex);
    const encryptedData = this.hexToArrayBuffer(encryptedHex);

    const key = await this.getKey();

    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      key,
      encryptedData,
    );

    return new TextDecoder().decode(decrypted);
  }

  private async getKey(): Promise<CryptoKey> {
    const base64Key = this.secretKey;
    const keyBuffer = this.base64ToArrayBuffer(base64Key);
    return window.crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-CBC' }, false, ['decrypt']);
  }

  private arrayBufferToHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private hexToArrayBuffer(hex: string): ArrayBuffer {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
